// Composer.tsx — Bottom input bar (V28 4-affordance composer, blueprint C2).
// Field + mic (iconbtn) + call (iconbtn) + CIRCLE gradient send.
//
// 2026-04-22: upgraded from single-line <input> to multi-line auto-grow <textarea>
// so long follow-up questions read comfortably (desktop especially, where you
// might type 2-3 sentences).
// 2026-04-22 (session 4): added voice-input mic button (it-IT Web Speech API).
// Mic is hidden entirely when the API isn't supported — no greyed-out UI.
// 2026-06-07 (V28-C): reshaped into the prototype's 4-affordance bar
// (docs/specs/v28-prototype/screens/prof.html lines 60-74 + kit.css .composer/
// .field/.iconbtn/.send). New optional onMic()/onCall() props let /prof route
// the mic icon to a voice-message and the call icon to a voice-call. When onMic
// is NOT provided the existing Web-Speech dictation fallback stays the mic's job
// (back-compat). The send button is now a violet-gradient CIRCLE with a glow.

import type { JSX } from 'react';
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useTokens } from '../state/theme';
import { useReducedMotion } from '../state/use-reduced-motion';
import { useSpeechRecognition } from '../utils/useSpeechRecognition';

interface ComposerProps {
  placeholder?: string;
  disabled?: boolean;
  voiceMode?: boolean;
  onSend?: (text: string) => void;
  /**
   * Mic-icon handler. When provided, the mic button becomes a plain trigger
   * (e.g. /prof → record a voice-message) and the built-in Web-Speech dictation
   * is NOT used. When omitted, the mic keeps the legacy dictation behaviour.
   */
  onMic?: () => void;
  /**
   * Call-icon handler (e.g. /prof → start a voice-call). The call button is
   * rendered only when this is provided — dictation-only callers don't get a
   * dead phone icon.
   */
  onCall?: () => void;
  /**
   * FP7 §1 (Mode B) — hold-to-talk voice note, LAYERED with V28 tap-to-dictate
   * (founder decision 2026-06-11). When provided, the mic gains a press-AND-HOLD
   * gesture: a quick TAP still toggles dictation (V28 default, unchanged), while a
   * press held past HOLD_THRESHOLD_MS records dictation into a buffer and, on
   * release, sends the captured transcript as a VOICE NOTE (onVoiceNote) — which
   * the host routes through the BE voiceNote path so Il Prof replies with a spoken
   * (CTRL.spoken) summary. Absent → the mic is tap-to-dictate only (no hold).
   */
  onVoiceNote?: (text: string) => void;
  /**
   * OA4 (D4 = option B, founder verbatim "option B") — dictation REVIEW mode.
   * When true, releasing a hold no longer auto-sends: the transcription lands
   * in the text box as an editable voice-note draft, and Invia is the separate
   * act that sends it (still through onVoiceNote, preserving the voice-note
   * origin and its budget). When false/absent, the FP7 Mode B auto-send
   * behaviour is byte-identical to before (the FE flag keeps this dark until
   * the cohort flips).
   */
  voiceNoteReview?: boolean;
}

// Textarea base height (single line). Max before scroll (~5 lines).
const MIN_HEIGHT = 40;
const MAX_HEIGHT = 140;

// FP7 §1 — tap vs hold threshold. A press shorter than this is a TAP (dictation
// toggle, V28 default); a press held past it is a HOLD (voice note). 350ms is long
// enough that a quick tap never records a note, short enough that an intentional
// hold engages almost immediately.
const HOLD_THRESHOLD_MS = 350;

function joinWithSpace(a: string, b: string): string {
  if (!a) return b;
  if (!b) return a;
  return a.endsWith(' ') ? a + b : a + ' ' + b;
}

export function Composer({
  placeholder = 'Chiedi qualsiasi cosa al Prof…',
  disabled,
  voiceMode = true,
  onSend,
  onMic,
  onCall,
  onVoiceNote,
  voiceNoteReview = false,
}: ComposerProps): JSX.Element {
  const tokens = useTokens();
  const reducedMotion = useReducedMotion();
  const [value, setValue] = useState('');
  // Live voice transcript — kept separate from `value` so the user sees it in
  // real time but doesn't have it "committed" into the editable buffer yet.
  // Folded into `value` either when a final result fires or when listening ends.
  const [interim, setInterim] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // When a custom mic handler is supplied the composer no longer owns dictation.
  const useDictation = !onMic;

  // FP7 §1 — hold-to-talk is available only when a voice-note sink is wired AND
  // the mic owns dictation (a custom onMic takes over the press entirely).
  const holdToTalkEnabled = Boolean(onVoiceNote) && useDictation;
  // While a HOLD is active the transcript accumulates here (NOT into the text box)
  // and is flushed as a voice note on release.
  const holdingRef = useRef(false);
  const voiceNoteBufferRef = useRef('');
  // OA4 — true while the text box holds an UNSENT voice-note draft (review
  // mode). Sending or clearing the field resets it; edits keep it (the note
  // stays a voice note per D4 option B).
  const [voiceNoteDraft, setVoiceNoteDraft] = useState(false);
  const holdTimerRef = useRef<number | null>(null);
  // True once a press has crossed HOLD_THRESHOLD_MS (it is a voice note, not a tap).
  const [isHolding, setIsHolding] = useState(false);

  const handleInterim = useCallback((text: string) => setInterim(text), []);
  const handleFinal = useCallback((text: string) => {
    if (holdingRef.current) {
      // Hold-to-talk: accumulate into the voice-note buffer, not the text box.
      voiceNoteBufferRef.current = joinWithSpace(voiceNoteBufferRef.current, text);
      setInterim('');
      return;
    }
    setValue((v) => joinWithSpace(v, text));
    setInterim('');
  }, []);

  const { supported: micSupported, listening, error: micError, start, stop, clearError } =
    useSpeechRecognition({
      onInterimResult: handleInterim,
      onFinalResult:   handleFinal,
    });
  // The dictation mic shows whenever the Web Speech API is supported — it is the
  // mic's job everywhere now (founder decision D-mic: the mic DICTATES into the
  // text field; the record→send "voice message" recorder was dropped). Visibility
  // is decoupled from `voiceMode` (the read-aloud fallback flag) so the dictation
  // mic stays present on a plain /prof landing. A custom onMic handler (legacy /
  // unused) always shows the mic (its support is the caller's job).
  const showMic = onMic ? true : micSupported;

  // When listening ends, fold any leftover interim into value. Rare — browsers
  // usually emit a final result on stop() — but covers user-cancel mid-utterance
  // and network glitches. (Only relevant in the dictation fallback path.)
  //
  // FP7 §1 — when listening ends during a HOLD, flush the buffered transcript as a
  // VOICE NOTE (not into the text box) instead.
  const prevListening = useRef(false);
  useEffect(() => {
    if (prevListening.current && !listening) {
      if (holdingRef.current) {
        holdingRef.current = false;
        setIsHolding(false);
        const note = joinWithSpace(voiceNoteBufferRef.current, interim).trim();
        voiceNoteBufferRef.current = '';
        setInterim('');
        if (note && !disabled) {
          if (voiceNoteReview) {
            // OA4 (D4 option B): the draft lands in the box for full editing;
            // send is a separate act (handleSend routes it as a voice note).
            setValue((v) => joinWithSpace(v, note));
            setVoiceNoteDraft(true);
          } else {
            onVoiceNote?.(note);
          }
        }
      } else if (interim) {
        setValue((v) => joinWithSpace(v, interim));
        setInterim('');
      }
    }
    prevListening.current = listening;
  }, [listening, interim, disabled, onVoiceNote]);

  const displayValue = interim ? joinWithSpace(value, interim) : value;

  // Auto-resize textarea to fit its content (up to MAX_HEIGHT, then scrolls).
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(Math.max(el.scrollHeight, MIN_HEIGHT), MAX_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, [displayValue]);

  function handleSend() {
    const trimmed = displayValue.trim();
    if (!trimmed || disabled) return;
    if (listening) stop();
    if (voiceNoteDraft) {
      // OA4 — the reviewed (possibly edited) draft still sends as a VOICE NOTE:
      // the origin and its budget survive the review (D4 option B contract).
      setVoiceNoteDraft(false);
      onVoiceNote?.(trimmed);
    } else {
      onSend?.(trimmed);
    }
    setValue('');
    setInterim('');
    inputRef.current?.focus();
  }

  function handleMic() {
    if (onMic) {
      onMic();
      return;
    }
    if (!micSupported) return;
    clearError();
    if (listening) stop();
    else           start();
  }

  // FP7 §1 — hold-to-talk press handlers (only wired when holdToTalkEnabled).
  // pointerDown arms a HOLD timer; if it fires, dictation starts recording into the
  // voice-note buffer. pointerUp BEFORE the timer fires is a plain TAP → normal
  // dictation toggle. pointerUp/leave/cancel AFTER the timer fired stops dictation,
  // and the listening-end effect flushes the buffer as a voice note.
  function clearHoldTimer() {
    if (holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }

  function handleMicPressStart() {
    if (!holdToTalkEnabled || !micSupported || disabled) return;
    // If dictation is already running (e.g. a prior tap), a press shouldn't hijack
    // it — let pointerUp fall through to the tap toggle (which stops it).
    if (listening) return;
    clearHoldTimer();
    holdTimerRef.current = window.setTimeout(() => {
      holdTimerRef.current = null;
      if (disabled) return;
      clearError();
      voiceNoteBufferRef.current = '';
      holdingRef.current = true;
      setIsHolding(true);
      start();
    }, HOLD_THRESHOLD_MS);
  }

  function handleMicPressEnd() {
    if (!holdToTalkEnabled) return;
    if (holdTimerRef.current !== null) {
      // Released before the threshold → this was a TAP. Treat as a dictation toggle.
      clearHoldTimer();
      handleMic();
      return;
    }
    if (holdingRef.current) {
      // A HOLD was in progress → stop dictation; the listening-end effect flushes
      // the captured transcript as a voice note.
      if (!listening) {
        holdingRef.current = false;
        setIsHolding(false);
        const note = joinWithSpace(voiceNoteBufferRef.current, interim).trim();
        voiceNoteBufferRef.current = '';
        setInterim('');
        if (note && !disabled) onVoiceNote?.(note);
        return;
      }
      stop();
      // Release ends the hold gesture immediately; real browsers still flush the
      // note on SpeechRecognition.onend, while tests with synchronous mocks need
      // this render to observe the stopped state.
      setIsHolding(false);
      return;
    }
    // No hold timer was armed and we are not holding — this is a TAP that started
    // while dictation was ALREADY listening (handleMicPressStart no-oped). Toggle
    // dictation off, exactly like the non-hold tap path.
    if (listening) handleMic();
  }

  // Refocus the input when the disabled prop flips from true → false
  // (i.e. the Prof just finished responding and is ready for a follow-up).
  const wasDisabled = useRef(disabled ?? false);
  useEffect(() => {
    const wasBlocked = wasDisabled.current;
    wasDisabled.current = disabled ?? false;
    if (wasBlocked && !disabled && document.visibilityState === 'visible') {
      const t = window.setTimeout(() => {
        const active = document.activeElement;
        const isInteracting =
          active instanceof HTMLElement &&
          active !== inputRef.current &&
          (active.tagName === 'BUTTON' || active.tagName === 'TEXTAREA' || active.tagName === 'INPUT');
        if (!isInteracting) inputRef.current?.focus({ preventScroll: true });
      }, 60);
      return () => window.clearTimeout(t);
    }
  }, [disabled]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends, Shift+Enter inserts a newline — standard chat-app convention.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // Clean up a dangling hold timer on unmount (e.g. pointerup never fires).
  useEffect(() => () => {
    if (holdTimerRef.current !== null) window.clearTimeout(holdTimerRef.current);
  }, []);

  const sendDisabled = disabled || listening || !displayValue.trim();
  const dictationListening = useDictation && listening;
  const effectivePlaceholder = isHolding
    ? 'Sto ascoltando… tieni premuto e parla.'
    : (dictationListening ? 'Sto ascoltando… parla pure.' : placeholder);
  // FP7 §1 — the mic label reflects the active gesture: a HOLD in progress prompts
  // release-to-send; otherwise hold-to-talk advertises the press affordance while
  // still supporting tap-to-dictate. Custom onMic + legacy paths unchanged.
  const micLabel = onMic
    ? 'Registra un messaggio vocale'
    : isHolding
      ? (voiceNoteReview ? 'Rilascia per rileggere la nota vocale' : 'Rilascia per inviare la nota vocale')
      : holdToTalkEnabled
        ? (listening ? 'Ferma dettatura' : 'Tocca per dettare o tieni premuto per una nota vocale')
        : (listening ? 'Ferma dettatura' : (voiceMode ? 'Parla' : 'Detta la tua domanda'));

  // Shared styling for the two transparent icon buttons (mic + call), matching
  // the prototype `.iconbtn`: 44×44 tap target, transparent fill, muted ink.
  const iconBtnStyle = (active: boolean) => ({
    width:          44,
    height:         44,
    borderRadius:   12,
    background:     active ? tokens.wrongSoft : 'transparent',
    color:          active ? tokens.wrong : tokens.textMuted,
    border:         'none',
    cursor:         disabled ? 'not-allowed' : 'pointer',
    flexShrink:     0,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        0,
    opacity:        disabled ? 0.55 : 1,
    transition:     reducedMotion ? undefined : 'background 120ms ease, color 120ms ease',
  } as const);

  return (
    <div style={{ flexShrink: 0 }}>
      {/* Error banner — shown when mic permission / network / hardware fails.
          Inline dismiss so students can clear and keep going. Dictation only. */}
      {/* OA4 — review-mode draft hint: the released transcription is in the
          box; sending is the separate act (D4 option B). */}
      {voiceNoteDraft && displayValue.trim() !== '' && (
        <div
          role="status"
          aria-live="polite"
          style={{
            margin: '0 4px 6px',
            fontSize: 12,
            color: tokens.textMuted,
            lineHeight: 1.3,
          }}
        >
          Nota vocale pronta: rileggila, modificala se serve, poi tocca Invia.
        </div>
      )}
      {useDictation && showMic && micError && micError.message && (
        <div
          role="status"
          aria-live="polite"
          style={{
            display:         'flex',
            alignItems:      'flex-start',
            justifyContent:  'space-between',
            gap:             10,
            padding:         '8px 14px',
            background:      tokens.wrongSoft,
            borderTop:       `1px solid ${tokens.line}`,
            color:           tokens.text,
            fontSize:        13,
            lineHeight:      1.4,
          }}
        >
          <span>{micError.message}</span>
          <button
            onClick={clearError}
            aria-label="Chiudi avviso"
            style={{
              background: 'transparent',
              border:     'none',
              color:      tokens.textFaint,
              cursor:     'pointer',
              fontSize:   16,
              lineHeight: 1,
              padding:    0,
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>
      )}

      <div
        style={{
          padding:       '10px 12px 14px',
          // V28 Phase F2: fold the bottom safe-area inset into the composer's
          // bottom padding so the input clears the home indicator on notched
          // devices. Longhand overrides the shorthand's bottom only.
          paddingBottom: 'calc(var(--sai-bottom) + 14px)',
          borderTop:  `1px solid ${tokens.line}`,
          // Redesign §3.7 / V4: frosted composer so the atmosphere shows through.
          background: tokens.mode === 'dark' ? 'rgba(22,16,31,0.5)' : 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          display:    'flex',
          gap:        8,
          alignItems: 'flex-end',
        }}
      >
        {/* Auto-grow multi-line input — the prototype `.field` pill. */}
        <textarea
          ref={inputRef}
          value={displayValue}
          onChange={(e) => {
            setValue(e.target.value);
            // OA4 — an emptied box is no longer a voice-note draft; edits
            // that keep text keep the voice-note origin (D4 option B).
            if (voiceNoteDraft && e.target.value.trim() === '') setVoiceNoteDraft(false);
          }}
          onKeyDown={handleKeyDown}
          placeholder={effectivePlaceholder}
          disabled={disabled}
          readOnly={dictationListening}
          rows={1}
          aria-label={effectivePlaceholder}
          style={{
            flex:         1,
            background:   tokens.surface,
            border:       `1px solid ${dictationListening ? tokens.wrong : tokens.lineStrong}`,
            borderRadius: tokens.radius.pill,
            padding:      '10px 16px',
            color:        tokens.text,
            fontFamily:   tokens.fonts.body,
            // R2-7 (V28 round-2): iOS Safari/Chrome (both WebKit) force-zoom the
            // page when a focused field's font-size is < 16px. 16px is the
            // canonical fix — never disable user zoom via the viewport meta
            // (that breaks accessibility / the dyslexia-support pinch-zoom).
            fontSize:     16,
            lineHeight:   1.4,
            minHeight:    MIN_HEIGHT,
            maxHeight:    MAX_HEIGHT,
            outline:      'none',
            opacity:      disabled ? 0.55 : 1,
            boxSizing:    'border-box',
            resize:       'none',
            overflow:     'hidden',
          }}
        />

        {/* Mic button — transparent icon button (prototype `.iconbtn`).
            Custom onMic → voice-message trigger; otherwise Web-Speech dictation
            (hidden entirely when SpeechRecognition is unsupported). */}
        {showMic && (
          <button
            // FP7 §1 — hold-to-talk: when enabled, the press is owned by the
            // pointer handlers (tap → dictation toggle, hold → voice note), so
            // onClick is suppressed to avoid a double-fire on release. When not
            // enabled, the mic stays a plain tap-to-dictate onClick (V28 default).
            onClick={holdToTalkEnabled ? undefined : handleMic}
            onPointerDown={holdToTalkEnabled ? (e) => { e.preventDefault(); handleMicPressStart(); } : undefined}
            onPointerUp={holdToTalkEnabled ? (e) => { e.preventDefault(); handleMicPressEnd(); } : undefined}
            onPointerLeave={holdToTalkEnabled ? () => handleMicPressEnd() : undefined}
            onPointerCancel={holdToTalkEnabled ? () => handleMicPressEnd() : undefined}
            disabled={disabled}
            className={(dictationListening || isHolding) ? 'asc-mic-pulsing' : undefined}
            style={{
              ...iconBtnStyle(dictationListening || isHolding),
              // Live dictation / hold-recording gets the violet pulse — suppressed
              // when the user (or OS) prefers reduced motion.
              animation: (dictationListening || isHolding) && !reducedMotion ? 'mic-pulse 1.4s infinite' : undefined,
              // Disable the browser's long-press context menu / text-selection so a
              // hold reads cleanly as a record gesture on touch.
              touchAction: holdToTalkEnabled ? 'none' : undefined,
              userSelect: holdToTalkEnabled ? 'none' : undefined,
              WebkitUserSelect: holdToTalkEnabled ? 'none' : undefined,
            }}
            aria-label={micLabel}
            aria-pressed={onMic ? undefined : (isHolding || listening)}
            title={micLabel}
          >
            {/* Microphone icon — 22px to match the prototype iconbtn glyphs. */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8"/>
            </svg>
          </button>
        )}

        {/* Call button — transparent icon button, rendered only when a call
            handler is supplied (/prof → start a voice-call). */}
        {onCall && (
          <button
            onClick={() => { if (!disabled) onCall(); }}
            disabled={disabled}
            style={iconBtnStyle(false)}
            aria-label="Chiama Il Prof"
            title="Chiama Il Prof"
          >
            {/* Phone icon — matches the prototype call glyph. */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/>
            </svg>
          </button>
        )}

        {/* Send button — CIRCLE gradient with a soft violet glow (prototype
            `.send`: border-radius 50%, var(--brand-grad), var(--shadow-violet)). */}
        <button
          onClick={handleSend}
          disabled={sendDisabled}
          style={{
            width:          44,
            height:         44,
            borderRadius:   '50%',
            // Redesign §3.3 / V4: gradient send button with a soft violet glow.
            background:     tokens.brandGradient,
            color:          '#fff',
            border:         'none',
            cursor:         sendDisabled ? 'not-allowed' : 'pointer',
            flexShrink:     0,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            padding:        0,
            opacity:        sendDisabled ? 0.5 : 1,
            boxShadow:      sendDisabled ? 'none' : '0 8px 22px rgba(105,67,255,0.28)',
            transition:     reducedMotion ? undefined : 'opacity 120ms ease, box-shadow 120ms ease',
          }}
          aria-label="Invia"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12"/>
            <polyline points="12 5 19 12 12 19"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
